import { addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase";

export type ListKind = "shopping" | "house";
export type HouseSchedule = "today" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday" | "soon" | "whenever";
export type HouseRecurrence = "none" | "daily" | "weekly";

export type HouseholdMember = {
  uid: string;
  firstName: string;
  lastName?: string;
  role?: string;
  avatar?: import("../components/AvatarEditor").AvatarConfig;
};

export type HouseTaskReward = {
  taskId: string;
  points: number;
  eligibleUid: string | null;
  recurrence?: HouseRecurrence;
  completionCount?: number;
  status: "open" | "pending" | "awarded";
  claimedBy?: string;
  awardedTo?: string;
};

export type FamilyListItem = {
  id: string;
  kind: ListKind;
  text: string;
  done: boolean;
  assigneeUid: string | null;
  schedule: HouseSchedule | null;
  createdBy: string;
  createdAt?: { toMillis?: () => number };
};

function cleanText(value: string) {
  return value.replace(/\s+/g, " ").trim().slice(0, 140);
}

export function subscribeToFamilyLists(householdId: string, onChange: (items: FamilyListItem[]) => void, onError: () => void) {
  return onSnapshot(collection(db, "households", householdId, "listItems"), (snapshot) => {
    const items = snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as FamilyListItem));
    items.sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
    onChange(items);
  }, onError);
}

export function subscribeToHouseholdMembers(householdId: string, onChange: (members: HouseholdMember[]) => void, onError: () => void) {
  return onSnapshot(collection(db, "households", householdId, "members"), (snapshot) => {
    const members = snapshot.docs.map((member) => ({ uid: member.id, ...member.data() } as HouseholdMember));
    members.sort((a, b) => a.firstName.localeCompare(b.firstName));
    onChange(members);
  }, onError);
}

export async function addFamilyListItem(householdId: string, userUid: string, input: {
  kind: ListKind;
  text: string;
  assigneeUid?: string | null;
  schedule?: HouseSchedule | null;
}) {
  const text = cleanText(input.text);
  if (!text) return;
  const created = await addDoc(collection(db, "households", householdId, "listItems"), {
    kind: input.kind,
    text,
    done: false,
    assigneeUid: input.kind === "house" ? input.assigneeUid || null : null,
    schedule: input.kind === "house" ? input.schedule || "whenever" : null,
    createdBy: userUid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return created.id;
}

export async function updateFamilyListItem(householdId: string, itemId: string, updates: Partial<Pick<FamilyListItem, "text" | "done" | "assigneeUid" | "schedule">>) {
  const next = { ...updates, updatedAt: serverTimestamp() } as Record<string, unknown>;
  if (typeof updates.text === "string") next.text = cleanText(updates.text);
  await updateDoc(doc(db, "households", householdId, "listItems", itemId), next);
}

export async function removeFamilyListItem(householdId: string, itemId: string) {
  await deleteDoc(doc(db, "households", householdId, "listItems", itemId));
}

export function subscribeToHouseTaskRewards(householdId: string, onChange: (rewards: HouseTaskReward[]) => void, onError: () => void) {
  return onSnapshot(collection(db, "households", householdId, "houseTaskRewards"), (snapshot) => {
    onChange(snapshot.docs.map((reward) => ({ taskId: reward.id, ...reward.data() } as HouseTaskReward)));
  }, onError);
}

export async function configureHouseTaskReward(householdId: string, taskId: string, points: number, notifyAssignee = false, recurrence: HouseRecurrence = "none") {
  const result = await httpsCallable<Record<string, unknown>, { notified: boolean }>(functions, "configureHouseTaskReward")({ householdId, taskId, points, notifyAssignee, recurrence });
  return result.data;
}

export async function claimHouseTaskReward(householdId: string, taskId: string) {
  await httpsCallable(functions, "claimHouseTaskReward")({ householdId, taskId });
}

export async function reviewHouseTaskReward(householdId: string, taskId: string, approve: boolean) {
  await httpsCallable(functions, "reviewHouseTaskReward")({ householdId, taskId, approve });
}

export async function notifyHouseTaskAssignment(householdId: string, taskId: string) {
  const result = await httpsCallable<{ householdId: string; taskId: string }, { notified: boolean }>(functions, "notifyHouseTaskAssignment")({ householdId, taskId });
  return result.data.notified;
}


export type HouseTaskHistoryResult = {
  memberUid: string;
  memberName: string;
  totalEarned: number;
  entries: Array<{ id: string; task: string; points: number; approvedAt: string | null; completionNumber: number | null }>;
};

export async function getHouseTaskHistory(householdId: string, memberUid: string) {
  const result = await httpsCallable<{ householdId: string; memberUid: string }, HouseTaskHistoryResult>(functions, "getHouseTaskHistory")({ householdId, memberUid });
  return result.data;
}
